/**
 * formatContainerForClipboard — format an entire project/list as markdown text.
 *
 * Output format:
 *   # Project Title
 *
 *   ## Section 1
 *   - [ ] Task under section
 *   - [x] Completed task
 *
 *   ## Section 2
 *   - [ ] Another task
 *
 * Used by Copy to Clipboard (Fas 25 §21) for containers.
 */

import { db, ObjectType, ObjectStatus } from "@/db/schema"

/**
 * Format a container (project or list) and its children as markdown.
 *
 * @param containerId — The ID of the project or list
 * @returns Formatted markdown string, or null if container not found
 */
export async function formatContainerForClipboard(
  containerId: string,
): Promise<string | null> {
  // Get the container itself
  const container = await db.totodo.get(containerId)
  if (!container?.title) return null

  // Query all non-deleted children (tasks + sections), sorted by index
  const parentPath = `root/${containerId}`
  const children = await db.totodo
    .where("[parentPath+isDeleted]")
    .equals([parentPath, 0])
    .filter(
      (item) =>
        item.type === ObjectType.Task || item.type === ObjectType.Section,
    )
    .sortBy("index")

  const lines: string[] = [`# ${container.title}`]

  for (const child of children) {
    if (child.type === ObjectType.Section) {
      // Blank line before section for readability
      lines.push("", `## ${child.title}`)
    } else if (child.status === ObjectStatus.Completed) {
      lines.push(`- [x] ${child.title}`)
    } else if (child.status === ObjectStatus.Canceled) {
      lines.push(`- [~] ${child.title}`)
    } else {
      lines.push(`- [ ] ${child.title}`)
    }
  }

  return lines.join("\n")
}
